import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { saFetch, SALoading, fmtMoney, inputCls, btnOutline, btnPrimary, FEATURE_LABELS } from "./saCommon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, PencilSimple, Trash, CircleNotch } from "@phosphor-icons/react";

const EMPTY = {
  name: "", description: "", monthly_price: "", annual_price: "", currency: "HNL",
  max_employees: 0, max_users: 0, features: [], is_free: false, trial_days: 0, tax_included: true, active: true,
};

export default function SAPlanes() {
  const [plans, setPlans] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState("");

  const load = useCallback(() => {
    saFetch("/superadmin/plans").then(setPlans).catch((e) => toast.error(e.message));
  }, []);
  useEffect(load, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, description: p.description || "", monthly_price: p.monthly_price, annual_price: p.annual_price,
      currency: p.currency || "HNL", max_employees: p.max_employees || 0, max_users: p.max_users || 0,
      features: p.features || [], is_free: !!p.is_free, trial_days: p.trial_days || 0,
      tax_included: p.tax_included !== false, active: p.active !== false,
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      monthly_price: Number(form.monthly_price) || 0,
      annual_price: Number(form.annual_price) || 0,
      max_employees: Number(form.max_employees) || 0,
      max_users: Number(form.max_users) || 0,
      trial_days: Number(form.trial_days) || 0,
    };
    try {
      if (editing) {
        await saFetch(`/superadmin/plans/${editing.id}`, { method: "PATCH", body: JSON.stringify(payload) });
        toast.success("Plan actualizado");
      } else {
        await saFetch("/superadmin/plans", { method: "POST", body: JSON.stringify(payload) });
        toast.success("Plan creado");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setDeleteError("");
    try {
      await saFetch(`/superadmin/plans/${toDelete.id}`, { method: "DELETE" });
      toast.success("Plan eliminado");
      setToDelete(null);
      load();
    } catch (e) {
      setDeleteError(e.message);
    }
  };

  const toggleActive = async (p) => {
    try {
      await saFetch(`/superadmin/plans/${p.id}`, { method: "PATCH", body: JSON.stringify({ ...p, active: !p.active }) });
      toast.success(p.active ? "Plan desactivado" : "Plan activado");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const toggleFeature = (f) =>
    setForm((prev) => ({ ...prev, features: prev.features.includes(f) ? prev.features.filter((x) => x !== f) : [...prev.features, f] }));

  if (!plans) return <SALoading label="Cargando planes..." />;

  return (
    <div data-testid="sa-planes">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-foreground">Planes y precios</h1>
          <p className="text-sm text-muted-foreground mt-1">Define los planes de suscripción de la plataforma</p>
        </div>
        <Button className={btnPrimary} data-testid="sa-new-plan" onClick={openCreate}>
          <Plus className="mr-2" size={15} /> Nuevo plan
        </Button>
      </div>

      {plans.length === 0 ? (
        <div className="border border-border rounded-sm p-10 text-center text-muted-foreground bg-card" data-testid="sa-plans-empty">
          Aún no hay planes. Crea el primero (por ejemplo: Gratuito, Emprendedor, Empresarial).
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="sa-plans-grid">
          {plans.map((p) => (
            <div key={p.id} className="border border-border bg-card rounded-sm p-5 hover:border-primary/50 transition-colors duration-200" data-testid={`sa-plan-card-${p.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-heading text-lg font-bold text-foreground flex items-center gap-2">
                    {p.name}
                    {p.is_free && <Badge variant="outline" className="rounded-sm border-emerald-500/40 text-emerald-500 font-normal">Gratis</Badge>}
                    {!p.active && <Badge variant="outline" className="rounded-sm border-border text-muted-foreground font-normal">Inactivo</Badge>}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{p.description || "Sin descripción"}</p>
                </div>
                <Switch checked={p.active !== false} onCheckedChange={() => toggleActive(p)} data-testid={`sa-plan-active-${p.id}`} />
              </div>
              <div className="mt-4 flex items-baseline gap-4">
                <div>
                  <p className="font-heading text-2xl font-bold text-foreground">{fmtMoney(p.monthly_price, p.currency)}</p>
                  <p className="text-[11px] text-muted-foreground">mensual</p>
                </div>
                <div>
                  <p className="font-heading text-lg font-semibold text-foreground/80">{fmtMoney(p.annual_price, p.currency)}</p>
                  <p className="text-[11px] text-muted-foreground">anual</p>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(p.features || []).map((f) => (
                  <span key={f} className="bg-primary/10 text-primary px-2 py-0.5 rounded-sm text-[11px]">{FEATURE_LABELS[f] || f}</span>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-3">
                {p.max_employees ? `Máx. ${p.max_employees} empleados` : "Empleados ilimitados"}
                {p.trial_days ? ` · ${p.trial_days} días de prueba` : ""}
                {` · ${p.companies_count || 0} empresa(s)`}
                {p.tax_included ? " · impuesto incluido" : " · + impuesto"}
              </p>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" size="sm" className={btnOutline} data-testid={`sa-plan-edit-${p.id}`} onClick={() => openEdit(p)}>
                  <PencilSimple className="mr-1.5" size={14} /> Editar
                </Button>
                <Button variant="outline" size="sm" className="rounded-sm border-red-900 text-red-400 hover:bg-red-950/40 hover:text-red-300" data-testid={`sa-plan-delete-${p.id}`} onClick={() => { setDeleteError(""); setToDelete(p); }}>
                  <Trash className="mr-1.5" size={14} /> Eliminar
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border rounded-sm sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">{editing ? "Editar plan" : "Nuevo plan"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="sa-plan-form">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Nombre *</Label>
              <Input className={inputCls} required data-testid="sa-plan-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Moneda</Label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger className={inputCls} data-testid="sa-plan-currency"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="HNL">Lempiras (HNL)</SelectItem>
                  <SelectItem value="USD">Dólares (USD)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Descripción</Label>
              <Textarea className={`${inputCls} min-h-[60px]`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Precio mensual</Label>
              <Input type="number" step="0.01" min="0" className={inputCls} data-testid="sa-plan-monthly" value={form.monthly_price} onChange={(e) => setForm({ ...form, monthly_price: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Precio anual</Label>
              <Input type="number" step="0.01" min="0" className={inputCls} data-testid="sa-plan-annual" value={form.annual_price} onChange={(e) => setForm({ ...form, annual_price: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Límite empleados (0 = ilimitado)</Label>
              <Input type="number" min="0" className={inputCls} value={form.max_employees} onChange={(e) => setForm({ ...form, max_employees: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Límite usuarios (0 = ilimitado)</Label>
              <Input type="number" min="0" className={inputCls} value={form.max_users} onChange={(e) => setForm({ ...form, max_users: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Días de prueba gratis</Label>
              <Input type="number" min="0" className={inputCls} value={form.trial_days} onChange={(e) => setForm({ ...form, trial_days: e.target.value })} />
            </div>
            <div className="space-y-3 pt-1">
              <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                <Switch checked={form.is_free} onCheckedChange={(v) => setForm({ ...form, is_free: v })} data-testid="sa-plan-free" /> Plan gratuito
              </label>
              <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                <Switch checked={form.tax_included} onCheckedChange={(v) => setForm({ ...form, tax_included: v })} data-testid="sa-plan-tax" /> Impuesto incluido en el precio
              </label>
            </div>
            <div className="sm:col-span-2 space-y-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Funciones incluidas</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {Object.entries(FEATURE_LABELS).map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2 text-xs text-foreground/80 cursor-pointer">
                    <Checkbox checked={form.features.includes(k)} onCheckedChange={() => toggleFeature(k)} className="border-input" data-testid={`sa-plan-feature-${k}`} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <DialogFooter className="sm:col-span-2">
              <Button type="submit" disabled={saving} className={btnPrimary} data-testid="sa-plan-save">
                {saving && <CircleNotch className="animate-spin mr-2" size={15} />} {editing ? "Guardar cambios" : "Crear plan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={() => setToDelete(null)}>
        <AlertDialogContent className="bg-card border-border rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-foreground">Eliminar plan</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              Se eliminará el plan "{toDelete?.name}".
              {toDelete?.companies_count > 0 && (
                <span className="block mt-2 text-amber-500">Tiene {toDelete.companies_count} empresa(s) asociada(s): deberás trasladarlas a otro plan primero.</span>
              )}
              {deleteError && <span className="block mt-2 text-red-400">{deleteError}</span>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent" data-testid="sa-plan-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="rounded-sm bg-red-600 text-white hover:bg-red-500" data-testid="sa-plan-delete-confirm">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
